import React, { useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

export type EmailPreference = 'preferred' | 'personal' | 'work'

export interface EmailPreferenceItem {
    preference: EmailPreference
    enabled: boolean
}

interface EmailPreferenceOrderProps {
    value: EmailPreferenceItem[]
    onChange: (newOrder: EmailPreferenceItem[]) => void
}

const EmailPreferenceOrder: React.FC<EmailPreferenceOrderProps> = ({
    value,
    onChange,
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const enabledItems = value.filter((item) => item.enabled)
    const disabledItems = value.filter((item) => !item.enabled)

    const moveUp = (index: number) => {
        if (index === 0) return
        const newEnabledItems = [...enabledItems]
        ;[newEnabledItems[index], newEnabledItems[index - 1]] = [
            newEnabledItems[index - 1],
            newEnabledItems[index],
        ]
        onChange([...newEnabledItems, ...disabledItems])
    }

    const moveDown = (index: number) => {
        if (index === enabledItems.length - 1) return
        const newEnabledItems = [...enabledItems]
        ;[newEnabledItems[index], newEnabledItems[index + 1]] = [
            newEnabledItems[index + 1],
            newEnabledItems[index],
        ]
        onChange([...newEnabledItems, ...disabledItems])
    }

    const toggleItem = (preference: EmailPreference) => {
        const newOrder = value.map((item) => {
            if (item.preference === preference) {
                const newEnabled = !item.enabled
                return { ...item, enabled: newEnabled }
            }
            return item
        })

        // If re-enabling, move to end of enabled list
        if (newOrder.find((item) => item.preference === preference)?.enabled) {
            const itemToMove = newOrder.find(
                (item) => item.preference === preference
            )!
            const otherItems = newOrder.filter(
                (item) => item.preference !== preference
            )
            const enabledOthers = otherItems.filter((item) => item.enabled)
            const disabledOthers = otherItems.filter((item) => !item.enabled)

            onChange([...enabledOthers, itemToMove, ...disabledOthers])
        } else {
            onChange(newOrder)
        }
    }

    const canDisable = (preference: EmailPreference) => {
        return enabledItems.length > 1
    }

    // Drag and drop handlers for swapping positions
    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', index.toString())
    }

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'

        // Set hover state when dragging over a different card
        if (
            draggedIndex !== null &&
            draggedIndex !== index &&
            hoveredIndex !== index
        ) {
            setHoveredIndex(index)
        }
    }

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault()

        if (draggedIndex !== null && draggedIndex !== dropIndex) {
            // Simple swap - exchange positions of dragged item and drop target
            const newEnabledItems = [...enabledItems]
            ;[newEnabledItems[draggedIndex], newEnabledItems[dropIndex]] = [
                newEnabledItems[dropIndex],
                newEnabledItems[draggedIndex],
            ]

            onChange([...newEnabledItems, ...disabledItems])
        }
        setDraggedIndex(null)
    }

    const handleDragEnd = () => {
        setDraggedIndex(null)
        setHoveredIndex(null)
    }

    const labels: Record<EmailPreference, string> = {
        preferred: 'Preferred Email',
        personal: 'Personal Email',
        work: 'Work Email',
    }

    return (
        <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Candidates will receive emails at the first available address in
                this order
            </Typography>

            {/* Enabled Preferences Section */}
            <Stack spacing={1}>
                {enabledItems.map((item, index) => (
                    <Card
                        key={item.preference}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        sx={{
                            border: '2px solid',
                            borderColor:
                                hoveredIndex === index
                                    ? 'primary.main'
                                    : 'divider',
                            opacity: draggedIndex === index ? 0.5 : 1,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            transition: 'opacity 0.2s, border-color 0.2s',
                        }}
                    >
                        <CardContent
                            sx={{
                                py: 1,
                                px: 2,
                                '&:last-child': { pb: 1 },
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{
                                            cursor: 'grab',
                                            '&:active': { cursor: 'grabbing' },
                                        }}
                                        aria-label={`Drag to reorder ${labels[item.preference]}`}
                                    >
                                        <DragIndicatorIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body1">
                                        {index + 1}. {labels[item.preference]}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0}>
                                    <IconButton
                                        size="small"
                                        onClick={() => moveUp(index)}
                                        disabled={index === 0}
                                        aria-label={`Move ${labels[item.preference]} up`}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => moveDown(index)}
                                        disabled={
                                            index === enabledItems.length - 1
                                        }
                                        aria-label={`Move ${labels[item.preference]} down`}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            toggleItem(item.preference)
                                        }
                                        disabled={!canDisable(item.preference)}
                                        aria-label={`Disable ${labels[item.preference]}`}
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {/* Disabled Preferences Section */}
            {disabledItems.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            mb: 1,
                            display: 'block',
                            color: 'text.secondary',
                        }}
                    >
                        Disabled
                    </Typography>
                    <Stack spacing={1}>
                        {disabledItems.map((item) => (
                            <Card
                                key={item.preference}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'action.hover',
                                    opacity: 0.6,
                                }}
                            >
                                <CardContent
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        '&:last-child': { pb: 1 },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                textDecoration: 'line-through',
                                                color: 'text.disabled',
                                            }}
                                        >
                                            {labels[item.preference]}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                toggleItem(item.preference)
                                            }
                                            aria-label={`Enable ${labels[item.preference]}`}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    )
}

export default EmailPreferenceOrder
